import {createIntl, createIntlCache} from 'react-intl';

import enAntdProvider from 'antd/locale/en_US';

import moment from 'moment';

import {getMomentLocale} from '@redux/modules/localization/utils';

import {horizonLocalStorage} from '@utils/local-storage';
import {LOCALES} from '@utils/locales/locales';

import enMessages from '../translations/en.json';

export const LOCAL_STORAGE_KEY = 'horizon-locale';
export const DEFAULT_LANGUAGE = LOCALES.EN;

/**
 * This class manages all localization file loading and access.
 *
 * It contains some utility methods to detect and store user language
 * as well as methods to async load message and local files for the
 * supported locales.
 *
 * Corresponding translation files can be found in /translations
 */
class Localization {
  constructor() {
    this.cache = createIntlCache();

    // Set english locale defaults synchronously
    this.setCachedLocaleData({
      locale: DEFAULT_LANGUAGE,
      messages: enMessages,
      antd: enAntdProvider,
    });
  }

  /**
   *
   * @returns {boolean}
   */
  requireIntlPolyfill = () => {
    return !window.Intl.PluralRules;
  };

  /**
   *
   * @param value
   * @returns {*}
   */
  isIntlMessageObject = (value) => {
    return !!value && !!value.id && !!value.defaultMessage;
  };

  /**
   * This method allows javascript translation of react-intl message objects
   * without the need to be in the react context or a component.
   *
   * Use this sparingly as it can result in additional coupling.
   *
   * @param message
   * @param data
   * @returns {string}
   */
  formatMessage = (message, data) => {
    return this.intl.formatMessage(message, data);
  };

  /**
   * Define user's language. Different browsers have the user locale defined
   * on different fields on the `navigator` object, so we make sure to account
   * for these different by checking all of them
   *
   * @returns {string}
   */
  getSystemLanguage = () => {
    const navigator = window.navigator;

    const globalLanguage = (navigator.languages && navigator.languages[0]) || navigator.language || DEFAULT_LANGUAGE;

    // Handle traditional chinese separately- check for zh-tw, zh-hk, or zh-Hant
    if (globalLanguage.match(/zh[_-](hant|hk|tw)/i)) {
      return LOCALES.ZH_HANT;
    } else if (globalLanguage.match(/zh[_-](hans|cn)/i)) {
      return LOCALES.ZH_HANS;
    }

    // Strip country and return base language only
    return globalLanguage.toLowerCase().split(/[_-]+/)[0];
  };

  /**
   * Returns the language value at LOCAL_STORAGE_KEY
   *
   * @returns {string}
   */
  getLocalStorageLanguage = () => {
    return horizonLocalStorage.getItem(LOCAL_STORAGE_KEY);
  };

  /**
   * Sets the language value at LOCAL_STORAGE_KEY
   *
   * @param language
   */
  setLocalStorageLanguage = (language) => {
    return horizonLocalStorage.setItem(LOCAL_STORAGE_KEY, language);
  };

  /**
   * Set the locale, messages, antd, and intl class values
   * and set the moment locale value.
   *
   * @param localeData
   * @returns {{
   *   antd: *,
   *   messages: *,
   *   locale: *,
   *   intl: IntlShape
   * }}
   */
  setCachedLocaleData = (localeData) => {
    // Set the moment locale
    moment.locale(getMomentLocale(localeData.locale));

    // Create an intl cache to drive translations
    const intl = createIntl(
      {
        locale: localeData.locale,
        messages: localeData.messages,
      },
      this.cache,
    );

    // Update class references
    this.locale = localeData.locale;
    this.language = localeData.language;
    this.messages = localeData.messages;
    this.antd = localeData.antd;
    this.intl = intl;

    return {
      locale: this.locale,
      language: this.language,
      messages: this.messages,
      antd: this.antd,
      intl: this.intl,
    };
  };

  /**
   *
   * @param language
   * @returns {Promise<{antd: *, messages: *, locale: *, intl: IntlShape}>}
   */
  updateLocaleDataAsync = async (language) => {
    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/polyfill');
      await import('@formatjs/intl-relativetimeformat/polyfill');
    }

    const localeData = await this.importLocaleDataByLanguage(language);

    return this.setCachedLocaleData(localeData);
  };

  /**
   * Asynchronously import and resolve the local info associated with the appropriate language.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importLocaleDataByLanguage = async (language) => {
    await import('react-moment');
    switch (language) {
      case LOCALES.JA:
        return this.importJapaneseLocale();
      case LOCALES.IT:
        return this.importItalianLocale();
      case LOCALES.FR:
        return this.importFrenchLocale();
      case LOCALES.ES:
        return this.importSpanishLocale();
      case LOCALES.DE:
        return this.importGermanLocale();
      case LOCALES.PT:
        return this.importPortugueseLocale();
      case LOCALES.ZH_HANS:
        return this.importChineseLocale();
      case LOCALES.ZH_HANT:
        return this.importChineseTraditionalLocale();
      case LOCALES.NL:
        return this.importDutchLocale();
      case LOCALES.PL:
        return this.importPolishLocale();
      case LOCALES.HI:
        return this.importHindiLocale();
      case LOCALES.RU:
        return this.importRussianLocale();
      case LOCALES.HE:
        return this.importHebrewLocale();
      case LOCALES.TR:
        return this.importTurkishLocale();
      case LOCALES.KO:
        return this.importKoreanLocale();
      case LOCALES.EL:
        return this.importGreekLocale();
      case LOCALES.VI:
        return this.importVietnameseLocale();
      case LOCALES.CS:
        return this.importCzechLocale();
      case LOCALES.HU:
        return this.importHungarianLocale();
      case LOCALES.MS:
        return this.importMalayLocale();
      case LOCALES.RO:
        return this.importRomanianLocale();
      case LOCALES.SK:
        return this.importSlovakLocale();
      case LOCALES.TH:
        return this.importThaiLocale();
      case LOCALES.UK:
        return this.importUkranianLocale();
      case LOCALES.BG:
        return this.importBulgarianLocale();
      case LOCALES.AR:
        return this.importArabicLocale();
      case LOCALES.SV:
        return this.importSwedishLocale();
      case LOCALES.FI:
        return this.importFinnishLocale();
      case LOCALES.DA:
        return this.importDanishLocale();
      case LOCALES.NO:
        return this.importNorwegianLocale();
      case LOCALES.CA:
        return this.importCatalanLocale();
      case LOCALES.FR_CA:
        return this.importFrenchCanadaLocale();
      case LOCALES.EN_GB:
        return this.importEnglishUKLocale();
      case LOCALES.PT_BR:
        return this.importPortugueseBrazilLocale();
      case LOCALES.ES_MX:
        return this.importSpanishMexicoLocale();
      case LOCALES.EN:
      default:
        return this.importEnglishLocale();
    }
  };

  /**
   * Asynchronously import English en_US formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importEnglishLocale = async () => {
    const locale = LOCALES.EN;
    const messages = await import('../translations/en.json');
    const antd = await import('antd/locale/en_US');

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/en');
      await import('@formatjs/intl-relativetimeformat/locale-data/en');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Spanish es_ES formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importSpanishLocale = async () => {
    const locale = LOCALES.ES;
    const messages = await import('../translations/es.json');
    const antd = await import('antd/locale/es_ES');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/es');
      await import('@formatjs/intl-relativetimeformat/locale-data/es');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Italian it_IT formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importItalianLocale = async () => {
    const locale = LOCALES.IT;
    const messages = await import('../translations/it.json');
    const antd = await import('antd/locale/it_IT');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/it');
      await import('@formatjs/intl-relativetimeformat/locale-data/it');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import French fr_FR formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importFrenchLocale = async () => {
    const locale = LOCALES.FR;
    const messages = await import('../translations/fr.json');
    const antd = await import('antd/locale/fr_FR');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/fr');
      await import('@formatjs/intl-relativetimeformat/locale-data/fr');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Japanese ja_JP formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importJapaneseLocale = async () => {
    const locale = LOCALES.JA;
    const messages = await import('../translations/ja.json');
    const antd = await import('antd/locale/ja_JP');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/ja');
      await import('@formatjs/intl-relativetimeformat/locale-data/ja');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import German de_DE formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importGermanLocale = async () => {
    const locale = LOCALES.DE;
    const messages = await import('../translations/de.json');
    const antd = await import('antd/locale/de_DE');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/de');
      await import('@formatjs/intl-relativetimeformat/locale-data/de');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Portuguese pt_BR formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importPortugueseLocale = async () => {
    const locale = LOCALES.PT;
    const messages = await import('../translations/pt.json');
    const antd = await import('antd/locale/pt_BR');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/pt');
      await import('@formatjs/intl-relativetimeformat/locale-data/pt');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Chinese zh_CN formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importChineseLocale = async () => {
    const locale = LOCALES.ZH_HANS;
    const messages = await import('../translations/zh-hans.json');
    const antd = await import('antd/locale/zh_CN');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/zh');
      await import('@formatjs/intl-relativetimeformat/locale-data/zh-Hans');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Chinese Traditional zh_TW formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importChineseTraditionalLocale = async () => {
    const locale = LOCALES.ZH_HANT;
    const messages = await import('../translations/zh-hant.json');
    const antd = await import('antd/locale/zh_TW');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/zh');
      await import('@formatjs/intl-relativetimeformat/locale-data/zh-Hant');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Dutch nl_NL formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importDutchLocale = async () => {
    const locale = LOCALES.NL;
    const messages = await import('../translations/nl.json');
    const antd = await import('antd/locale/nl_NL');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/nl');
      await import('@formatjs/intl-relativetimeformat/locale-data/nl');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Polish pl_PL formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importPolishLocale = async () => {
    const locale = LOCALES.PL;
    const messages = await import('../translations/pl.json');
    const antd = await import('antd/locale/pl_PL');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/pl');
      await import('@formatjs/intl-relativetimeformat/locale-data/pl');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Hindi hi_IN formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importHindiLocale = async () => {
    const locale = LOCALES.HI;
    const messages = await import('../translations/hi.json');
    const antd = await import('antd/locale/hi_IN');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/hi');
      await import('@formatjs/intl-relativetimeformat/locale-data/hi');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Russian ru_RU formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importRussianLocale = async () => {
    const locale = LOCALES.RU;
    const messages = await import('../translations/ru.json');
    const antd = await import('antd/locale/ru_RU');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/ru');
      await import('@formatjs/intl-relativetimeformat/locale-data/ru');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Hebrew he_IL formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importHebrewLocale = async () => {
    const locale = LOCALES.HE;
    const messages = await import('../translations/he.json');
    const antd = await import('antd/locale/he_IL');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/he');
      await import('@formatjs/intl-relativetimeformat/locale-data/he');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Turkish tr_TR formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importTurkishLocale = async () => {
    const locale = LOCALES.TR;
    const messages = await import('../translations/tr.json');
    const antd = await import('antd/locale/tr_TR');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/tr');
      await import('@formatjs/intl-relativetimeformat/locale-data/tr');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Korean ko_KR formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importKoreanLocale = async () => {
    const locale = LOCALES.KO;
    const messages = await import('../translations/ko.json');
    const antd = await import('antd/locale/ko_KR');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/ko');
      await import('@formatjs/intl-relativetimeformat/locale-data/ko');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Greek el_GR formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importGreekLocale = async () => {
    const locale = LOCALES.EL;
    const messages = await import('../translations/el.json');
    const antd = await import('antd/locale/el_GR');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/el');
      await import('@formatjs/intl-relativetimeformat/locale-data/el');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Vietnamese vi_VN formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importVietnameseLocale = async () => {
    const locale = LOCALES.VI;
    const messages = await import('../translations/vi.json');
    const antd = await import('antd/locale/vi_VN');
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/vi');
      await import('@formatjs/intl-relativetimeformat/locale-data/vi');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Czech cs_CZ formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importCzechLocale = async () => {
    const locale = LOCALES.CS;
    const messages = await import('../translations/cs.json');
    const antd = await import('antd/locale/cs_CZ');

    // Import moment Czech locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/cs');
      await import('@formatjs/intl-relativetimeformat/locale-data/cs');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Hungarian hu_HU formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importHungarianLocale = async () => {
    const locale = LOCALES.HU;
    const messages = await import('../translations/hu.json');
    const antd = await import('antd/locale/hu_HU');

    // Import moment Hungarian locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/hu');
      await import('@formatjs/intl-relativetimeformat/locale-data/hu');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Malay ms_MY formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importMalayLocale = async () => {
    const locale = LOCALES.MS;
    const messages = await import('../translations/ms.json');
    const antd = await import('antd/locale/ms_MY');

    // Import moment Malay locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/ms');
      await import('@formatjs/intl-relativetimeformat/locale-data/ms');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Romanian ro_RO formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importRomanianLocale = async () => {
    const locale = LOCALES.RO;
    const messages = await import('../translations/ro.json');
    const antd = await import('antd/locale/ro_RO');

    // Import moment Romanian locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/ro');
      await import('@formatjs/intl-relativetimeformat/locale-data/ro');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Slovak sk_SK formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importSlovakLocale = async () => {
    const locale = LOCALES.SK;
    const messages = await import('../translations/sk.json');
    const antd = await import('antd/locale/sk_SK');

    // Import moment Slovak locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/sk');
      await import('@formatjs/intl-relativetimeformat/locale-data/sk');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Thai th_TH formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importThaiLocale = async () => {
    const locale = LOCALES.TH;
    const messages = await import('../translations/th.json');
    const antd = await import('antd/locale/th_TH');

    // Import moment Thai locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/th');
      await import('@formatjs/intl-relativetimeformat/locale-data/th');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Ukrainian uk_UA formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importUkranianLocale = async () => {
    const locale = LOCALES.UK;
    const messages = await import('../translations/uk.json');
    const antd = await import('antd/locale/uk_UA');

    // Import moment Ukrainian locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/uk');
      await import('@formatjs/intl-relativetimeformat/locale-data/uk');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Bulgarian bg_BG formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importBulgarianLocale = async () => {
    const locale = LOCALES.BG;
    const messages = await import('../translations/bg.json');
    const antd = await import('antd/locale/bg_BG');

    // Import moment Bulgarian locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/bg');
      await import('@formatjs/intl-relativetimeformat/locale-data/bg');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Arabic ar_EG formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importArabicLocale = async () => {
    const locale = LOCALES.AR;
    const messages = await import('../translations/ar.json');
    const antd = await import('antd/locale/ar_EG');
    // Import moment Arabic locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/ar');
      await import('@formatjs/intl-relativetimeformat/locale-data/ar');
    }

    return {
      locale: locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Swedish sv_SE formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importSwedishLocale = async () => {
    const locale = LOCALES.SV;
    const messages = await import('../translations/sv.json');
    const antd = await import('antd/locale/sv_SE');
    // Import moment Swedish locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/sv');
      await import('@formatjs/intl-relativetimeformat/locale-data/sv');
    }

    return {
      locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Finnish fi_FI formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importFinnishLocale = async () => {
    const locale = LOCALES.FI;
    const messages = await import('../translations/fi.json');
    const antd = await import('antd/locale/fi_FI');
    // Import moment Finnish locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/fi');
      await import('@formatjs/intl-relativetimeformat/locale-data/fi');
    }

    return {
      locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Danish da_DK formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importDanishLocale = async () => {
    const locale = LOCALES.DA;
    const messages = await import('../translations/da.json');
    const antd = await import('antd/locale/da_DK');
    // Import moment Danish locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/da');
      await import('@formatjs/intl-relativetimeformat/locale-data/da');
    }

    return {
      locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Norwegian nb_NO formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importNorwegianLocale = async () => {
    const locale = LOCALES.NO;
    const messages = await import('../translations/no.json');
    // Import moment Norwegian locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    const antd = await import('antd/locale/nb_NO');

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/nb');
      await import('@formatjs/intl-relativetimeformat/locale-data/nb');
    }

    return {
      locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Catalan ca_ES formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importCatalanLocale = async () => {
    const locale = LOCALES.CA;
    const messages = await import('../translations/ca.json');
    const antd = await import('antd/locale/ca_ES');
    // Import moment Catalan locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/ca');
      await import('@formatjs/intl-relativetimeformat/locale-data/ca');
    }

    return {
      locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import French Canadian fr_CA formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importFrenchCanadaLocale = async () => {
    const locale = LOCALES.FR_CA;
    const messages = await import('../translations/fr-CA.json');
    const antd = await import('antd/locale/fr_CA');
    // Import moment French Canadian locale data
    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/fr');
      await import('@formatjs/intl-relativetimeformat/locale-data/fr-CA');
    }

    return {
      locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import English UK en_GB formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importEnglishUKLocale = async () => {
    const locale = LOCALES.EN_GB;
    const messages = await import('../translations/en-GB.json');
    const antd = await import('antd/locale/en_GB');

    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/en');
      await import('@formatjs/intl-relativetimeformat/locale-data/en-GB');
    }

    return {
      locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Portuguese Brazil pt_BR formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importPortugueseBrazilLocale = async () => {
    const locale = LOCALES.PT_BR;
    const messages = await import('../translations/pt-BR.json');
    const antd = await import('antd/locale/pt_BR');

    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/pt');
      await import('@formatjs/intl-relativetimeformat/locale-data/pt-PT');
    }

    return {
      locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };

  /**
   * Asynchronously import Spanish Mexico es_MX formats.
   *
   * @returns {Promise<{antd: *, data: *, messages: *, locale: string}>}
   */
  importSpanishMexicoLocale = async () => {
    const locale = LOCALES.ES_MX;
    const messages = await import('../translations/es-MX.json');
    const antd = await import('antd/locale/es_ES');

    const momentLocale = getMomentLocale(locale);
    await import(`moment/locale/${momentLocale}`);

    if (this.requireIntlPolyfill()) {
      await import('@formatjs/intl-pluralrules/locale-data/es');
      await import('@formatjs/intl-relativetimeformat/locale-data/es-MX');
    }

    return {
      locale,
      language: locale,
      messages: messages.default,
      antd: antd.default,
    };
  };
}

// Export singleton instance of class
export default new Localization();
